/**
 * useDeferredListDate
 * Lets the date strip commit a new day at once while the heavy games list renders a frame later
 * (specs/challenge-flow-redesign AC-3.7). While `isSwitchingDate` is true the list should show its
 * skeleton, not the previous day's matches.
 *
 * Deliberately not `useDeferredValue`: on web, JS-driven Animated loops (e.g. the skeleton shimmer)
 * re-render every frame at a higher priority and starve the deferred render for ~5s.
 */

import { useEffect, useState } from 'react';

/** Upper bound on the skeleton frame when rAF never fires. */
const RAF_FALLBACK_MS = 100;

interface UseDeferredListDateReturn {
  /** The day the list has caught up to (lags `selectedDate` during a switch). */
  listDate: Date;
  /** True between a day tap and the list render for that day. */
  isSwitchingDate: boolean;
}

export const useDeferredListDate = (selectedDate: Date): UseDeferredListDateReturn => {
  const [listDate, setListDate] = useState(selectedDate);
  const isSwitchingDate = listDate.getTime() !== selectedDate.getTime();

  useEffect(() => {
    if (!isSwitchingDate) return;
    // rAF then a macrotask: the skeleton frame paints before the heavy list render starts.
    // The fallback timer covers environments where rAF is paused (hidden web tab, headless).
    const catchUp = () => setListDate(selectedDate);
    let afterFrame: ReturnType<typeof setTimeout> | undefined;
    const frame = requestAnimationFrame(() => {
      afterFrame = setTimeout(catchUp, 0);
    });
    const fallback = setTimeout(catchUp, RAF_FALLBACK_MS);
    return () => {
      cancelAnimationFrame(frame);
      if (afterFrame !== undefined) clearTimeout(afterFrame);
      clearTimeout(fallback);
    };
  }, [isSwitchingDate, selectedDate]);

  return { listDate, isSwitchingDate };
};
